import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { LocalUser } from '../models/local-user.model';
import { environment } from '../../../environments/environment';

export interface UserSearchResult {
  Id: number;
  Name: string;
  FirstName: string;
  LastName: string;
  Email: string;
  JobTitle: string | null;
  Company: string | null;
  Mobile: string | null;
  Telephone: string | null;
  /** Present on the full-profile response (`getByEmail`) — omitted from `search` results. */
  ThumbnailPhoto?: string | null;
  ThumbnailPhotoMime?: string | null;
  /** Present on `getBySupplier` results (existing supplier contacts) — matches legacy `UserModelApi`. */
  SupplierLastName?: string | null;
  SupplierCompany?: string | null;
  SupplierCountry?: string | null;
  /** Present on the full-profile response (`getByEmail`) — matches legacy `UserModelApi.LanguageCode`. */
  LanguageCode?: string | null;
}

/** Matches legacy `UserModelApi` — the shape returned by `GET /Users/` (admin user list)
 *  and `PATCH /Users/{userId}/role`. */
export interface UserModel {
  Id: number;
  Name: string;
  FirstName: string;
  LastName: string;
  Email: string;
  JobTitle: string;
  Department: string;
  Company: string;
  ThumbnailPhoto: string;
  ThumbnailPhotoMime: string;
  Mobile: string;
  Telephone: string;
  OfficeCity: string;
  OfficeStreet: string;
  OfficeZipCode: string;
  OfficeState: string;
  OfficeCountry: string;
  UserRole: 'SuperUser' | 'Administrator' | 'User' | 'Supplier';
  IsGod: boolean;
  Suppliers: string[];
  LanguageCode: string;
  SupplierLastName: string;
  SupplierCompany: string;
  SupplierCountry: string;
}

/** Matches legacy `UpdateUserRoleRequestApi`. */
export interface UpdateUserRoleRequest {
  Role: 'SuperUser' | 'Administrator' | 'User' | 'Supplier';
}

/** Maps a raw `UserModelApi`-shaped response to the app's `LocalUser`, matching legacy
 *  `AuthenticationService.isAdmin`/`isSupplier` getters exactly. Exported for reuse/testing. */
export function mapToLocalUser(user: UserModel): LocalUser {
  return {
    Email: user.Email,
    Name: user.Name,
    displayName: user.Name,
    languageCode: user.LanguageCode,
    jobTitle: user.JobTitle ?? '',
    department: user.Department ?? '',
    company: user.Company ?? '',
    role: user.UserRole,
    isCreator: false,
    isReviewer: false,
    isOwner: false,
    isClaimOwner: false,
    isSupplier: user.UserRole === 'Supplier',
    isAdmin: user.UserRole === 'Administrator' || user.IsGod,
  };
}

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly http = inject(HttpClient);

  /**
   * GET /Users/{email}/ — the current user's full profile (Name/Email/JobTitle/Department/
   * Company/UserRole/IsGod/LanguageCode), mapped to `LocalUser`.
   * Bug fix: previously called GET /Users/{email}/localUser, but legacy's
   * `AuthenticationService.ensureUserIsLoaded()` — the single source of truth for the popover's
   * Name/JobTitle/Department/Company/isAdmin/isSupplier — actually calls
   * `userApiService.getByEmail()` (`GET /Users/{email}/`), NOT `GetLocalUserByEmail()`
   * (`GET /Users/{email}/localUser`). Legacy only ever calls the `/localUser` endpoint once, in
   * the auth guard, and only reads its `LanguageCode` field — every other field on that response
   * (JobTitle/Department/Company/UserRole/IsGod) is discarded by legacy and never relied upon.
   * Role-derivation rules unchanged, matching legacy `AuthenticationService.isAdmin`/`isSupplier`
   * getters: `isAdmin = UserRole === 'Administrator' || IsGod` (SuperUser is NOT admin),
   * `isSupplier = UserRole === 'Supplier'`.
   */
  getLocalUser(email: string): Observable<LocalUser> {
    const url = `${environment.apiBaseUrl}/Users/${encodeURIComponent(email)}/`;
    return this.http.get<UserModel>(url, {
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    }).pipe(map(mapToLocalUser));
  }

  updateLanguage(email: string, language: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiBaseUrl}/Users/${encodeURIComponent(email)}/language`,
      { Language: language },
    );
  }

  /** A80 - GET /Users/?searchText=...&includeMembers=...&includeGuests=...&supplierName=... */
  search(
    searchText: string,
    includeMembers = true,
    includeGuests = false,
    supplierName = ''
  ): Observable<UserSearchResult[]> {
    const params = new HttpParams()
      .set('searchText', searchText)
      .set('includeMembers', String(includeMembers))
      .set('includeGuests', String(includeGuests))
      .set('supplierName', supplierName);
    return this.http.get<UserSearchResult[]>(`${environment.apiBaseUrl}/Users/`, { params });
  }

  /** GET /Users/{email}/ — full user profile (Name, Mobile, Telephone) for a known email address. */
  getByEmail(email: string): Observable<UserSearchResult> {
    const url = `${environment.apiBaseUrl}/Users/${encodeURIComponent(email)}/`;
    return this.http.get<UserSearchResult>(url);
  }

  /** GET /Suppliers/{supplierName}/Users — existing user contacts for a given supplier
   *  (used to populate the "choose from existing supplier contacts" list). */
  getBySupplier(supplierName: string): Observable<UserSearchResult[]> {
    const url = `${environment.apiBaseUrl}/Suppliers/${encodeURIComponent(supplierName)}/Users`;
    return this.http.get<UserSearchResult[]>(url);
  }

  /** GET /Users/ — full admin user list (Administration > User Management grid). */
  getAllUsers(): Observable<UserModel[]> {
    return this.http.get<UserModel[]>(`${environment.apiBaseUrl}/Users/`);
  }

  /** PATCH /Users/{userId}/role — admin role update (Administration > User Management). */
  updateRole(userId: number, model: UpdateUserRoleRequest): Observable<UserModel> {
    return this.http.patch<UserModel>(`${environment.apiBaseUrl}/Users/${userId}/role`, model);
  }
}
